// src/components/Dashboard/PerformanceCharts.js - FIXED VERSION
import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';
import { theme, rgba } from '../../theme';

const PerformanceCharts = ({ data }) => {
  // Real current snapshot only - no historical revenue tracking exists yet
  // anywhere in the data model (see MEMORY: financial_reporting_dashboard_scoping),
  // so previous months are never fabricated here. Once real historical data exists,
  // this becomes an actual multi-point trend instead of a single "Current" point.
  const performanceData = useMemo(() => {
    if (data?.analytics && data.analytics.monthlyRevenue > 0) {
      return [{
        name: 'Current',
        revenue: data.analytics.monthlyRevenue || 0,
        employees: data.employees?.length || 0,
        sites: data.analytics.totalSites || 0,
        efficiency: Math.round(data.analytics.profitMargin || 0)
      }];
    }

    return [];
  }, [data?.analytics?.monthlyRevenue, data?.employees?.length, data?.analytics?.totalSites, data?.analytics?.profitMargin]);

  // Generate industry data based on actual clients
  const industryData = useMemo(() => {
    if (data?.analytics?.clientDetails?.length > 0) {
      const industryCount = data.analytics.clientDetails.reduce((acc, client) => {
        const type = client.type || 'Security Services';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});
      
      const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
      const total = Object.values(industryCount).reduce((sum, count) => sum + count, 0);
      
      return Object.entries(industryCount).map(([name, count], index) => ({
        name,
        value: Math.round((count / total) * 100),
        color: colors[index % colors.length]
      }));
    }
    
    return [];
  }, [data?.analytics?.clientDetails]);

  return (
    <div style={{ 
      display: 'grid', 
      gridTemplateColumns: '2fr 1fr', 
      gap: '24px', 
      marginBottom: '32px' 
    }}>
      {/* Performance Trend Chart */}
      <div style={{
        background: rgba(theme.colors.tileBackgroundRgb, 0.8),
        border: `1px solid ${rgba(theme.colors.tileBorderRgb, 0.3)}`,
        borderRadius: '16px',
        padding: '24px',
        backdropFilter: 'blur(12px)'
      }}>
        <h2 style={{ 
          color: '#e2e8f0', 
          fontSize: '22px', 
          marginBottom: '20px', 
          fontWeight: '600'
        }}>
          Performance Trends
        </h2>
        {performanceData.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={performanceData}>
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(59, 130, 246, 0.1)" />
              <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
              <YAxis stroke="#94a3b8" fontSize={12} />
              <Tooltip
                contentStyle={{
                  background: rgba(theme.colors.tileBackgroundRgb, 0.95),
                  border: `1px solid ${rgba(theme.colors.tileBorderRgb, 0.3)}`,
                  borderRadius: '8px',
                  color: '#e2e8f0',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
                }}
                formatter={(value, name) => [
                  name === 'revenue' ? `$${value.toLocaleString()}` : value,
                  name === 'revenue' ? 'Revenue' :
                  name === 'employees' ? 'Employees' :
                  name === 'sites' ? 'Sites' :
                  name === 'efficiency' ? 'Efficiency %' : name
                ]}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#3b82f6"
                fill="url(#revenueGradient)"
                strokeWidth={3}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#6b7280' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📈</div>
            <p>No revenue data available</p>
            <p style={{ fontSize: '12px', marginTop: '8px' }}>
              Add revenue data to see performance trends
            </p>
          </div>
        )}
      </div>

      {/* Industry Mix Chart */}
      <div style={{
        background: rgba(theme.colors.tileBackgroundRgb, 0.8),
        border: `1px solid ${rgba(theme.colors.tileBorderRgb, 0.3)}`,
        borderRadius: '16px',
        padding: '24px',
        backdropFilter: 'blur(12px)'
      }}>
        <h2 style={{ 
          color: '#e2e8f0', 
          fontSize: '22px', 
          marginBottom: '20px', 
          fontWeight: '600' 
        }}>
          Industry Mix
        </h2>
        
        {industryData.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={industryData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {industryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{
                    background: rgba(theme.colors.tileBackgroundRgb, 0.95),
                    border: `1px solid ${rgba(theme.colors.tileBorderRgb, 0.3)}`,
                    borderRadius: '8px',
                    color: '#e2e8f0'
                  }}
                  formatter={(value) => [`${value}%`, 'Share']}
                />
              </PieChart>
            </ResponsiveContainer>
            
            <div style={{ marginTop: '16px' }}>
              {industryData.map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 0',
                  borderBottom: index < industryData.length - 1 ? `1px solid ${rgba(theme.colors.tileBorderRgb, 0.1)}` : 'none'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: item.color
                    }} />
                    <span style={{ color: '#e2e8f0', fontSize: '14px' }}>{item.name}</span>
                  </div>
                  <span style={{ color: '#94a3b8', fontSize: '14px', fontWeight: '500' }}>
                    {item.value}%
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '32px 0', color: '#6b7280' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
            <p>No client data available</p>
            <p style={{ fontSize: '12px', marginTop: '8px' }}>
              Add clients to see industry breakdown
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceCharts;